import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUser, AppUserAuth, AuthService } from '@ubie/core';
import { FormHelper } from '../../../helpers/form-helper';

@Component({
  selector: 'shared-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  private readonly securityService = inject(AuthService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  /**
   * Event that triggers when a user is logged in
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onUserLoggedIn: EventEmitter<any> = new EventEmitter();

  /**
   * Event that triggers when the register button is clicked
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onRegisterClicked: EventEmitter<any> = new EventEmitter();

  /**
   * Event that triggers when the forgot password button is clicked
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onForgotPasswordClicked: EventEmitter<any> =
    new EventEmitter();

  /**
   * Use this to set the logo on the login component
   */
  _logo = 'https://imgplaceholder.com/300x160';
  @Input()
  set logo(data: string) {
    this._logo = data;
  }
  /**
   * Use showRegister="True" to enable the register button on the form
   */
  @Input() showRegister?: boolean;
  /**
   * Use showForgotPassword="True" to enable the forgot password button on the form
   */
  @Input() showForgotPassword?: boolean;
  /**
   * Use showFullPage="True" to show the component page filling
   */
  @Input() showFullPage?: boolean;

  loginFailed = false;
  formSubmitAttempted = false;
  user: AppUser = new AppUser();
  securityObject: AppUserAuth | null = null;
  returnUrl: string | null = null;
  form?: UntypedFormGroup;
  formHelper?: FormHelper;
  formIsFocused?: boolean;

  constructor() {
    this.initForm();
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    // fetch the return url from the route
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
  }

  /**
   * Init the form
   */
  initForm(): void {
    // is only initiated once
    if (this.form === undefined) {
      this.form = this.formBuilder.group({
        username: [null, [Validators.required, Validators.email]],
        password: [null, Validators.required],
      });

      // init form helper
      this.formHelper = new FormHelper(this.form);
    }
  }

  /**
   * Register button is clicked
   */
  onRegisterCl(): void {
    this.onRegisterClicked.emit();
  }

  /**
   * Forgot password button is clicked
   */
  onForgotPasswordCl(): void {
    this.onForgotPasswordClicked.emit();
  }

  /**
   * Login button is clicked
   */
  onSubmit(): void {
    this.formSubmitAttempted = true;
    this.loginFailed = false;

    if (this.form?.valid) {
      // Copy the form values over the object values
      const obj = Object.assign({}, this.user, this.form.value);

      this.securityService.login(obj).subscribe(
        (data) => {
          this.securityObject = data;
          if (this.returnUrl) {
            // Emit user logged in event
            this.onUserLoggedIn.emit();
            // Navigate to the returnurl
            this.router.navigateByUrl(this.returnUrl);
          }
        },
        (error) => {
          // Reset security object
          this.securityObject = new AppUserAuth();
          // Show error message
          this.loginFailed = true;
        },
      );
    }
  }

  /**
   * From the moment an input field is focused, create a compact version of the page
   */
  shrink(shrink: boolean): void {
    // set bool to true in order to change the css class of the form and make it more compact
    this.formIsFocused = shrink;
  }
}
